/**
 * I prodotti selezionati che il report non puo' riportare perche' la loro
 * quantita' e' sotto zero.
 *
 * **Perche' una guardia prima dell'invio e non la lettura di un rifiuto.**
 * L'api rifiuta gia': `Zdhc\InventoryController` valida il `weight` con `min:0`
 * e una regex senza segno. Ma quel rifiuto e' un `validation_error`, che
 * l'interceptor di `useApi` assorbe **risolvendo** col numero `422`: chi ha
 * chiamato riceve `null` e a schermo resta «ricarica la pagina», alla fine di
 * un periodo di lavoro. E anche arrivandoci con il messaggio giusto, l'api
 * rifiuta l'**intero payload** e ragiona per indice di riga: non conosce i
 * nostri prodotti, quindi non potrebbe nominare quello da correggere. Il client
 * invece ha in mano le quantita' da cui calcola i pesi, cioe' tutto quello che
 * serve per dirlo prima di partire.
 *
 * Sta in un modulo e non nel modale per la ragione dei moduli gemelli
 * (`inventoryPeriod`, `duplicateReportGroups`): sul client non c'e' un runner
 * di test, quindi cio' che va provato deve essere importabile da un harness.
 */

/**
 * Il peso in kg che una reference porta nel payload dell'inventario.
 *
 * La quantita' e' memorizzata in centesimi (`quantity * 100` su ogni writer
 * dell'api), quindi la divisione e' la conversione, e l'arrotondamento a due
 * decimali e' il formato che il Gateway accetta.
 *
 * **Esportata perche' e' la stessa aritmetica in due posti**: qui, per decidere
 * se una riga e' negativa, e nel modale, per costruire il `weight` da mandare.
 * Se divergessero, il numero mostrato nel messaggio non sarebbe piu' il numero
 * rifiutato — cioe' l'utente correggerebbe una cifra diversa da quella che
 * blocca il report.
 *
 * `reference?.quantity` e non `reference.quantity`: l'espressione originale
 * sollevava su una reference assente. Non e' raggiungibile dal modale — le
 * guardie sul match la dereferenziano prima — ma un `TypeError` non e' un
 * comportamento su cui appoggiarsi, e qui una reference assente non e' una
 * quantita' negativa: e' zero, e non va segnalata.
 */
export const weightOf = (reference) =>
    Math.round((reference?.quantity / 100 || 0) * 100) / 100;

/**
 * I prodotti con quantita' negativa, nell'ordine in cui sono stati scelti.
 *
 * **Solo fra i selezionati**, perche' solo quelli diventano righe del report:
 * un prodotto negativo che l'utente ha lasciato fuori non blocca niente, e
 * segnalarlo aggiungerebbe un ostacolo dove oggi non ce n'e' uno.
 *
 * Lo zero non e' negativo e resta valido: e' una correzione legittima, ed e' il
 * valore che l'api scrive quando la quantita' e' assente. Il vincolo qui e' lo
 * stesso del `weight` a cui la quantita' diventa, non uno piu' stretto.
 *
 * @param {Array} products i prodotti selezionati, ognuno con la sua
 *        `incheck_reference` gia' risolta
 * @returns {Array} `{ name: string|null, weight: number }` — `name` porta
 *          `null` dove il nome non si sa (la colonna e' nullable), e a decidere
 *          come scriverlo e' chi rende il testo, che ha le traduzioni
 */
export const negativeQuantityProducts = (products) =>
    (Array.isArray(products) ? products : [])
        .map( (product) => ({
            name: typeof product?.name === 'string' && product.name.trim() !== ''
                ? product.name.trim()
                : null,
            weight: weightOf(product?.incheck_reference)
        }))
        // `< 0` e non `<= 0`: sullo zero il report parte. Un `NaN` — quantita'
        // non numerica — non e' negativo e non finisce qui: lo lascia passare
        // all'api, che e' l'autorita' sul formato del peso.
        .filter( (line) => line.weight < 0);

export default negativeQuantityProducts;
